import React, { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Grid from '@mui/material/Grid';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconsSend from '@mui/icons-material/Send';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import DesktopDatePicker from '@mui/lab/DesktopDatePicker';
import CircularProgress from '@mui/material/CircularProgress';
import FormHelperText from '@mui/material/FormHelperText';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import { makeStyles } from '@mui/styles';
import { Theme } from '@mui/material/styles';
import InfoIcon from '@mui/icons-material/Info';
import Context from '../../../../../context';
import useNotification from '../../../../../components/_hook/useNotification';
import { useUpdateExpiryDateCompanySubdirectorySubscriptionMutation } from '../../../../../../graphql/mutation/companySubdirectorySubscription/updateExpiryDateCompanySubdirectorySubscriptionMutation';
import { useCompanySubdirectorySubscriptionQuery } from '../../../../../../graphql/query/companySubdirectorySubscription/useCompanySubdirectorySubscriptionQuery';
import ButtonSubmit from '../../../../../components/_button/ButtonSubmit';
import useForm from '../../../../../components/_hook/useForm';
import NotFound from '../../../../../components/_error/NotFound';
import SchemaCompanyName from '../../../../../components/_schema/SchemaCompanyName';
import SuperAdminPermission from '../../../../../adminComponents/_permission/SuperAdminPermission';
import Moment from '../../../../../components/Moment';

interface IFormValue {
  expiryDate: Date | null;
}

interface IProps {
  companySubdirectorySubscriptionId: string;
  onCancel: () => void;
  onComplete?: () => void;
}

const useStyles = makeStyles<Theme>((theme: Theme) => ({
  paper: {
    margin: theme.spacing(1),
    padding: theme.spacing(3),
    width: '100%',
  },
}));

export default function FormManagePlanSubdirectorySubscription(props: IProps) {
  const classes = useStyles();
  const { t } = useTranslation();
  const { companySubdirectorySubscriptionId, onCancel, onComplete } = props;
  const { notification } = useNotification();

  const fragment = `
          _id
          company
          status
          expiryDate
          subdirectoryData {
            _id
            company
            companyData {
              _id
              name {
                  la
                  text
              }
              origin
              slug
            }
           
            status
            domain
          }
  `;
  const {
    data: companySubdirectorySubscriptionData,
    loading: companySubdirectorySubscriptionLoading,
  } = useCompanySubdirectorySubscriptionQuery(fragment, {
    variables: {
      filter: {
        _id: companySubdirectorySubscriptionId,
      },
      limit: 1,
    },
  });

  const [
    updateExpiryDateCompanySubdirectorySubscriptionMutation,
    { loading: isUpdatingExpiryDateCompanySubdirectorySubscription },
  ] = useUpdateExpiryDateCompanySubdirectorySubscriptionMutation(
    `updatedCompanySubdirectorySubscription {
				${fragment}
			}`,
    {
      onCompleted: async () => {
        notification.success(t('success'));
        if (onComplete) {
          await onComplete();
        }
      },
      onError: (e) => {
        notification.error(e.message);
      },
    }
  );

  async function onSubmitForm(values: IFormValue) {
    if (companySubdirectorySubscriptionData) {
      const tempCompanySubdirectorySubscription =
        companySubdirectorySubscriptionData.companySubdirectorySubscriptions
          .items[0];

      if (tempCompanySubdirectorySubscription) {
        await updateExpiryDateCompanySubdirectorySubscriptionMutation({
          variables: {
            companySubdirectorySubscriptionId,
            expiryDate: values.expiryDate as Date,
          },
        });
      }
    }
  }

  const {
    values,
    setValue,
    handleSubmit,
    errors,
    setValues,
    isSubmitting,
  } = useForm<IFormValue>({
    defaultValues: {
      expiryDate: null,
    },
    onSubmit: onSubmitForm,
    onValidate: (tempValues) => {
      const newErrors: Record<string, string> = {};

      if (!tempValues.expiryDate) {
        newErrors.expiryDate = t('please_enter_the_correct__type__', {
          type: t('expiry_date'),
        });
      }

      return newErrors;
    },
  });

  useEffect(() => {
    if (
      !companySubdirectorySubscriptionLoading &&
      companySubdirectorySubscriptionData &&
      companySubdirectorySubscriptionData.companySubdirectorySubscriptions.items
        .length
    ) {
      const companySubdirectorySubscription =
        companySubdirectorySubscriptionData.companySubdirectorySubscriptions
          .items[0];

      setValues({
        expiryDate: companySubdirectorySubscription.expiryDate || null,
      });
    }
  }, [companySubdirectorySubscriptionLoading]);

  if (companySubdirectorySubscriptionLoading) {
    return (
      <Grid container justifyContent="center">
        <CircularProgress size={30} color="inherit" />
      </Grid>
    );
  }

  const companySubdirectorySubscription = companySubdirectorySubscriptionData
    ? companySubdirectorySubscriptionData.companySubdirectorySubscriptions
        .items[0]
    : null;

  if (!companySubdirectorySubscription) {
    return <NotFound />;
  }

  return (
    <>
      <DialogTitle>
        {t('manage')} {t('subdirectory')} {t('subscription')}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="subtitle1">
              {t('subdirectory')} {t('domain')}:&nbsp;
              {companySubdirectorySubscription.subdirectoryData && (
                <Button
                  color="primary"
                  component="a"
                  href={`//${companySubdirectorySubscription.subdirectoryData.domain[0]}`}
                  target="_blank"
                >
                  <Typography>
                    {companySubdirectorySubscription.subdirectoryData.domain[0]}
                  </Typography>
                </Button>
              )}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle1">
              {t('subdirectory')} {t('company')}:&nbsp;
              {companySubdirectorySubscription.subdirectoryData &&
                companySubdirectorySubscription.subdirectoryData
                  .companyData && (
                  <SchemaCompanyName
                    useIndexLink
                    company={
                      companySubdirectorySubscription.subdirectoryData
                        .companyData
                    }
                  />
                )}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle1">
              {t('subdirectory')} {t('status')}:&nbsp;
              {companySubdirectorySubscription.subdirectoryData &&
              companySubdirectorySubscription.subdirectoryData.status
                ? t('active')
                : t('inactive')}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle1">
              {t('expiry_date')}:{' '}
              <Moment
                dateOnly
                time={companySubdirectorySubscription.expiryDate}
              />
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle1">
              {t('subscription')} {t('status')}:&nbsp;
              {companySubdirectorySubscription.status
                ? t('active')
                : t('inactive')}
            </Typography>
          </Grid>
          <SuperAdminPermission hideTooltip>
            <Paper square className={classes.paper}>
              <Grid container item xs={12} spacing={2}>
                <Grid item xs={12} container alignItems="center">
                  <InfoIcon />
                  &nbsp;
                  <Typography variant="subtitle1">
                    {t('only_super_admin_will_see_this')} {t('section')}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <DesktopDatePicker
                      renderInput={(tempProps) => <TextField {...tempProps} />}
                      label={t('expiry_date')}
                      value={values.expiryDate}
                      onChange={(date) => setValue('expiryDate', date || null)}
                    />
                    <FormHelperText error>{errors.expiryDate}</FormHelperText>
                  </FormControl>
                </Grid>
                <Grid container item xs={12} justifyContent="flex-end">
                  <ButtonSubmit
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit}
                    isSubmitting={
                      isUpdatingExpiryDateCompanySubdirectorySubscription
                    }
                    label={t('update')}
                    icon={<IconsSend />}
                  />
                </Grid>
              </Grid>
            </Paper>
          </SuperAdminPermission>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Grid container spacing={2} justifyContent="flex-end">
          <Grid item>
            <Button variant="text" onClick={onCancel}>
              {t('cancel')}
            </Button>
          </Grid>
        </Grid>
      </DialogActions>
    </>
  );
}
